import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Mail, Lock, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function signupWithGoogle() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(email, password, name);
      toast.success("Account created!");
      navigate(u.role === "owner" ? "/dashboard/setup" : "/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto grid min-h-[70vh] max-w-md place-items-center px-5 py-16 md:px-8 md:py-24">
      <div className="w-full rounded-2xl border border-cyan-100 bg-white p-7 shadow-sm md:p-9">
        <h1 className="font-display text-3xl font-bold text-slate-900">Create your account</h1>
        <p className="mt-1 text-sm text-slate-500">Track your quotes, orders, and service history.</p>

        <button
          type="button"
          onClick={signupWithGoogle}
          className="mt-6 flex w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 hover:border-cyan-300 hover:bg-cyan-50"
          data-testid="signup-google-button"
        >
          Continue with Google
        </button>

        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400">
          <div className="h-px flex-1 bg-slate-200" /> or <div className="h-px flex-1 bg-slate-200" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Full name</Label>
            <div className="relative mt-1">
              <UserIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" required value={name} onChange={(e) => setName(e.target.value)} data-testid="signup-name-input" />
            </div>
          </div>
          <div>
            <Label>Email</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-email-input" />
            </div>
          </div>
          <div>
            <Label>Password (min. 6 chars)</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" type="password" minLength={6} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signup-password-input" />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="btn-aqua w-full" data-testid="signup-submit-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Create account
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-500">
          Already have an account? <Link to="/login" className="font-medium text-cyan-700 hover:underline" data-testid="signup-login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
